import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Paper,
  Snackbar,
  Divider,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Upload as UploadIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';

// Use environment variable for API key
const API_KEY = process.env.REACT_APP_API_KEY || 'UXvTRtMeXRZTtZgMNZhht_kYcLB.KcqxkVjX2.qmTQ';

const OtherPricesPanel = ({ onPricesUpdated, open, onClose, isModal = false }) => {
  // State for labor costs
  const [laborCosts, setLaborCosts] = useState({
    iOS: { nok: 850, sek: 850, dkk: 600, eur: 85 },
    iPad: { nok: 600, sek: 600, dkk: 600, eur: 60 },
    Mac: { nok: 960, sek: 960, dkk: 760, eur: 96 },
    Beats: { nok: 400, sek: 400, dkk: 300, eur: 30 },
    AirPods: { nok: 300, sek: 300, dkk: 200, eur: 30 },
    Watch: { nok: 400, sek: 400, dkk: 400, eur: 40 }
  });

  // State for freight costs
  const [freightCosts, setFreightCosts] = useState({
    iOS: { nok: 160, sek: 160, dkk: 90.4, eur: 16 },
    iPad: { nok: 160, sek: 160, dkk: 90.4, eur: 16 },
    Mac: { nok: 224, sek: 224, dkk: 128, eur: 24 },
    Beats: { nok: 160, sek: 160, dkk: 90.4, eur: 16 },
    AirPods: { nok: 160, sek: 160, dkk: 90.4, eur: 16 },
    Watch: { nok: 160, sek: 160, dkk: 90.4, eur: 16 }
  });

  // State for freight type (1-way or 2-way)
  const [freightType, setFreightType] = useState('2way');

  // State for labor costs Excel import
  const [laborCostFile, setLaborCostFile] = useState(null);
  const [isImportingLabor, setIsImportingLabor] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Add state for tracking original database values and changes
  const [otherPrices, setOtherPrices] = useState([]);
  const [isDataChanged, setIsDataChanged] = useState(false);
  
  // Fetch other prices from the database when component mounts
  useEffect(() => {
    fetchOtherPrices();
  }, []);

  // Get auth headers function
  const getAuthHeaders = (contentType = 'application/json') => {
    const headers = { 
      'Content-Type': contentType,
      'X-API-Key': API_KEY.trim()
    };
    
    // Get JWT token from cookies
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('token='));
    if (tokenCookie) {
      const token = tokenCookie.split('=')[1];
      // If token contains semicolon (additional cookie information), strip it
      const cleanToken = token.includes(';') ? token.split(';')[0] : token;
      headers['Authorization'] = `Bearer ${cleanToken}`;
    } else {
      // Try localStorage as fallback
      const localToken = localStorage.getItem('token');
      if (localToken) {
        headers['Authorization'] = `Bearer ${localToken}`;
      }
    }
    
    // For FormData, we need to let the browser set the Content-Type correctly
    if (contentType === 'multipart/form-data') {
      delete headers['Content-Type'];
    }
    
    return headers;
  };

  // Function to fetch other prices from the database
  const fetchOtherPrices = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = getAuthHeaders();
      console.log("Fetching other prices with headers:", headers);
      
      const response = await axios.get(
        `/priceadmin/all-other-prices`, 
        { headers }
      );
      
      console.log("Other prices received:", response.data);
      
      if (response.data && response.data.prices) {
        setOtherPrices(response.data.prices);
        
        // Convert database format to component state format
        const dbLaborCosts = {};
        const dbFreightCosts1way = {};
        const dbFreightCosts2way = {};
        
        // Process each price entry
        response.data.prices.forEach(price => {
          const { category, price_type, currency, amount } = price;
          
          if (!category || !price_type || !currency) return;
          
          // Format the category for our state (capitalize first letter)
          const formattedCategory = category.charAt(0).toUpperCase() + category.slice(1);
          
          if (price_type === 'labor') {
            if (!dbLaborCosts[formattedCategory]) {
              dbLaborCosts[formattedCategory] = { nok: 0, sek: 0, dkk: 0, eur: 0 };
            }
            dbLaborCosts[formattedCategory][currency] = amount;
          } else if (price_type === 'freight_1way') {
            if (!dbFreightCosts1way[formattedCategory]) {
              dbFreightCosts1way[formattedCategory] = { nok: 0, sek: 0, dkk: 0, eur: 0 };
            }
            dbFreightCosts1way[formattedCategory][currency] = amount;
          } else if (price_type === 'freight_2way') {
            if (!dbFreightCosts2way[formattedCategory]) {
              dbFreightCosts2way[formattedCategory] = { nok: 0, sek: 0, dkk: 0, eur: 0 };
            }
            dbFreightCosts2way[formattedCategory][currency] = amount;
          }
        });
        
        // Update state with database values but keep structure
        // Make sure all categories exist in our state
        const updatedLaborCosts = {...laborCosts};
        const updatedFreightCosts = {...freightCosts};
        
        // Update labor costs from DB
        Object.entries(dbLaborCosts).forEach(([category, costs]) => {
          if (updatedLaborCosts[category]) {
            updatedLaborCosts[category] = {
              ...updatedLaborCosts[category],
              ...costs
            };
          }
        });
        
        // Update freight costs from DB based on current freightType
        const dbFreightCosts = freightType === '2way' ? dbFreightCosts2way : dbFreightCosts1way;
        
        Object.entries(dbFreightCosts).forEach(([category, costs]) => {
          if (updatedFreightCosts[category]) {
            updatedFreightCosts[category] = {
              ...updatedFreightCosts[category],
              ...costs
            };
          }
        });
        
        setLaborCosts(updatedLaborCosts);
        setFreightCosts(updatedFreightCosts);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(`Failed to fetch other prices: ${errorMsg}`);
      console.error('Error fetching other prices:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle labor cost changes
  const handleLaborCostChange = (category, currency, value) => {
    const numValue = parseFloat(value) || 0;
    setLaborCosts(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [currency]: numValue
      }
    }));
    setIsDataChanged(true);
  };

  // Handle freight cost changes
  const handleFreightCostChange = (category, currency, value) => {
    const numValue = parseFloat(value) || 0;
    setFreightCosts(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [currency]: numValue
      }
    }));
    setIsDataChanged(true);
  };

  // Change freight type
  const handleFreightTypeChange = (event) => {
    const newFreightType = event.target.checked ? '2way' : '1way';
    setFreightType(newFreightType);
    setIsDataChanged(true);
    
    // Re-fetch prices with the new freight type to get correct values
    fetchOtherPrices();
  };

  // Handle file selection for labor cost import
  const handleLaborCostFileChange = (e) => {
    setLaborCostFile(e.target.files?.[0] || null);
  };
  
  // Show snackbar message
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Import labor costs from Excel
  const handleImportLaborCosts = async () => {
    if (!laborCostFile) {
      setError('Please select an Excel file with labor costs');
      return;
    }
    
    if (!laborCostFile.name.endsWith('.xlsx') && !laborCostFile.name.endsWith('.xls')) {
      setError('File must be an Excel (.xlsx or .xls) file');
      return;
    }
    
    setIsImportingLabor(true);
    setError(null);
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', laborCostFile);
      
      const headers = getAuthHeaders('multipart/form-data');
      
      console.log("Sending request to import labor costs");
      
      const response = await axios.post(
        '/priceadmin/import-labor-costs', 
        formData,
        { headers }
      );
      
      console.log("Import response:", response.data);
      
      if (response.data) {
        // Initialize with default values
        const newLaborCosts = {...laborCosts};
        const newFreightCosts = {...freightCosts};
        
        // Update labor costs from the server response
        if (response.data.labor_costs) {
          console.log("Received labor costs:", response.data.labor_costs);
          const importedLaborCosts = response.data.labor_costs;
          
          // Map the backend keys to frontend keys
          const keyMapping = {
            'ios': 'iOS',
            'ipad': 'iPad',
            'mac': 'Mac',
            'beats': 'Beats',
            'airpods': 'AirPods',
            'watch': 'Watch'
          };
          
          // Update labor costs with imported values for all categories
          Object.keys(keyMapping).forEach(backendKey => {
            const frontendKey = keyMapping[backendKey];
            if (importedLaborCosts[backendKey]) {
              console.log(`Updating ${frontendKey} labor costs:`, importedLaborCosts[backendKey]);
              // Make sure to copy all currency values
              newLaborCosts[frontendKey] = {
                nok: importedLaborCosts[backendKey].nok || newLaborCosts[frontendKey].nok,
                sek: importedLaborCosts[backendKey].sek || newLaborCosts[frontendKey].sek,
                dkk: importedLaborCosts[backendKey].dkk || newLaborCosts[frontendKey].dkk,
                eur: importedLaborCosts[backendKey].eur || newLaborCosts[frontendKey].eur
              };
            }
          });
        }
        
        // Update freight costs based on selected type
        const freightData = freightType === '1way' ? 
          response.data.freight_costs_1way : 
          response.data.freight_costs_2way;
          
        if (freightData) {
          console.log(`Received ${freightType} freight costs:`, freightData);
          
          // Map the backend keys to frontend keys
          const keyMapping = {
            'ios': 'iOS',
            'ipad': 'iPad',
            'mac': 'Mac',
            'beats': 'Beats',
            'airpods': 'AirPods',
            'watch': 'Watch'
          };
          
          // Update freight costs with imported values for all categories
          Object.keys(keyMapping).forEach(backendKey => {
            const frontendKey = keyMapping[backendKey];
            if (freightData[backendKey]) {
              console.log(`Updating ${frontendKey} freight costs:`, freightData[backendKey]);
              // Make sure to copy all currency values
              newFreightCosts[frontendKey] = {
                nok: freightData[backendKey].nok || newFreightCosts[frontendKey].nok,
                sek: freightData[backendKey].sek || newFreightCosts[frontendKey].sek,
                dkk: freightData[backendKey].dkk || newFreightCosts[frontendKey].dkk,
                eur: freightData[backendKey].eur || newFreightCosts[frontendKey].eur
              };
            }
          });
        }
        
        // Actually update the state with new values
        console.log("Updating state with new labor costs:", newLaborCosts);
        console.log("Updating state with new freight costs:", newFreightCosts);
        
        setLaborCosts(newLaborCosts);
        setFreightCosts(newFreightCosts);
        
        showSnackbar('Labor and freight costs imported successfully', 'success');
        setLaborCostFile(null);
        
        // Notify parent component about the update
        if (onPricesUpdated) {
          onPricesUpdated({
            laborCosts: newLaborCosts,
            freightCosts: newFreightCosts,
            freightType
          });
        }
        
        // Clear the file input
        if (document.getElementById('laborCostFileUpload')) {
          document.getElementById('laborCostFileUpload').value = '';
        }
      } else {
        setError('No valid costs found in the file');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message;
      setError(`Failed to import costs: ${errorMsg}`);
      console.error('Error importing costs:', err);
    } finally {
      setIsImportingLabor(false);
      setLoading(false);
    }
  };

  // Save all prices manually
  const handleSaveOtherPrices = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = getAuthHeaders();
      
      // Convert our state format to database format
      const pricesToSave = [];
      
      // Add labor costs
      Object.entries(laborCosts).forEach(([category, costs]) => {
        Object.entries(costs).forEach(([currency, amount]) => {
          pricesToSave.push({
            category: category.toLowerCase(),
            price_type: 'labor',
            currency,
            amount
          });
        });
      });
      
      // Add both 1-way and 2-way freight costs to ensure both are saved
      Object.entries(freightCosts).forEach(([category, costs]) => {
        Object.entries(costs).forEach(([currency, amount]) => {
          pricesToSave.push({
            category: category.toLowerCase(),
            price_type: 'freight_1way',
            currency,
            amount: freightType === '1way' ? amount : amount / 2 // Adjust for 1-way
          });
          
          pricesToSave.push({
            category: category.toLowerCase(),
            price_type: 'freight_2way',
            currency,
            amount: freightType === '2way' ? amount : amount * 2 // Adjust for 2-way
          });
        });
      });
      
      console.log("Saving other prices:", pricesToSave);
      
      // Send data to backend
      const response = await axios.post(
        '/priceadmin/save-other-prices',
        { prices: pricesToSave },
        { headers }
      );
      
      console.log("Save response:", response.data);
      showSnackbar('Other prices saved successfully', 'success');
      setIsDataChanged(false);
      
      // Notify parent component about the update
      if (onPricesUpdated) {
        onPricesUpdated({
          laborCosts,
          freightCosts,
          freightType
        });
      }
      
      // Refresh the data
      fetchOtherPrices();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(`Failed to save other prices: ${errorMsg}`);
      console.error('Error saving other prices:', err);
    } finally {
      setLoading(false);
    }
  };

  // Transform labor costs into rows for DataGrid
  const getLaborCostRows = () => {
    return Object.entries(laborCosts).map(([category, costs]) => ({
      id: `labor-${category}`,
      category,
      type: 'Labor Cost',
      nok: costs.nok,
      sek: costs.sek,
      dkk: costs.dkk,
      eur: costs.eur
    }));
  };

  // Transform freight costs into rows for DataGrid
  const getFreightCostRows = () => {
    return Object.entries(freightCosts).map(([category, costs]) => ({
      id: `freight-${category}`,
      category,
      type: freightType === '2way' ? '2-way Freight' : '1-way Freight',
      nok: costs.nok,
      sek: costs.sek,
      dkk: costs.dkk,
      eur: costs.eur
    }));
  };

  // DataGrid columns definition
  const costColumns = [
    { 
      field: 'category', 
      headerName: 'Device', 
      width: 100, 
      editable: false 
    },
    { 
      field: 'type', 
      headerName: 'Type', 
      width: 120, 
      editable: false 
    },
    { 
      field: 'nok', 
      headerName: 'NOK', 
      width: 100, 
      editable: true,
      type: 'number'
    },
    { 
      field: 'sek', 
      headerName: 'SEK', 
      width: 100, 
      editable: true,
      type: 'number'
    },
    { 
      field: 'dkk', 
      headerName: 'DKK', 
      width: 100, 
      editable: true,
      type: 'number'
    },
    { 
      field: 'eur', 
      headerName: 'EUR', 
      width: 100, 
      editable: true, 
      type: 'number'
    }
  ];

  // Handle cell edit for DataGrid
  const handleCellEditCommit = (params) => {
    const { id, field, value } = params;
    
    // Check if this is a labor or freight cost
    if (id.startsWith('labor-')) {
      const category = id.replace('labor-', '');
      handleLaborCostChange(category, field, value);
    } else if (id.startsWith('freight-')) {
      const category = id.replace('freight-', '');
      handleFreightCostChange(category, field, value);
    }
  };

  return (
    <>
      {isModal ? (
        <Dialog 
          open={open} 
          onClose={onClose} 
          maxWidth="lg" 
          fullWidth
        >
          <DialogTitle>
            Labor & Freight Cost Management
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {/* Main content */}
            {renderContent()}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit">
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleSaveOtherPrices();
                onClose();
              }}
              startIcon={<SaveIcon />}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Box className="other-prices-panel">
          {renderContent()}
        </Box>
      )}
    </>
  );

  // Helper function to render the main content
  function renderContent() {
    return (
      <>
        {!isModal && (
          <Typography variant="h5" component="h2" gutterBottom>
            Labor & Freight Costs Management
          </Typography>
        )}
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 4 }}>
          {/* Only show upload sections in modal view */}
          {isModal && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  Import Other Prices Excel
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={freightType === '2way'}
                      onChange={handleFreightTypeChange}
                    />
                  }
                  label="Use 2-way freight (otherwise 1-way)"
                />
              </Box>
              
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Select Excel File
                  <input
                    id="laborCostFileUpload"
                    type="file"
                    hidden
                    onChange={handleLaborCostFileChange}
                    accept=".xlsx,.xls"
                  />
                </Button>
                
                {laborCostFile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={laborCostFile.name} 
                      onDelete={() => setLaborCostFile(null)}
                    />
                    <Button 
                      variant="outlined"
                      color="primary"
                      onClick={handleImportLaborCosts}
                      disabled={isImportingLabor || loading}
                      startIcon={isImportingLabor ? <CircularProgress size={20} /> : null}
                    >
                      {isImportingLabor ? 'Importing...' : 'Import'}
                    </Button>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  File Format Requirements
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Typography>
                
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Column</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Example Values</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Brand/type</TableCell>
                        <TableCell>Device brand</TableCell>
                        <TableCell>Apple</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Desciption</TableCell>
                        <TableCell>Price type</TableCell>
                        <TableCell>Repair price - Standard, 1-way freight, 2-way freight</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Model</TableCell>
                        <TableCell>Device category</TableCell>
                        <TableCell>iphone, ipad, macbook, beats, airpods, iwatch</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Price NOK/SEK/DKK/EUR</TableCell>
                        <TableCell>Price values</TableCell>
                        <TableCell>850, 600, 90.4, etc.</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              
              <Divider sx={{ my: 3 }} />
            </>
          )}
          
          {/* Show the DataGrid in both views */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {isModal ? 'Current Values:' : 'Labor & Freight Costs'}
            </Typography>
            
            {!isModal && (
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={freightType === '2way'}
                    onChange={handleFreightTypeChange}
                  />
                }
                label="Use 2-way freight (otherwise 1-way)"
              />
            )}
          </Box>
          
          <Box sx={{ height: 400, width: '100%', mb: isModal ? 4 : 0 }}>
            <DataGrid
              rows={[...getLaborCostRows(), ...getFreightCostRows()]}
              columns={costColumns}
              disableRowSelectionOnClick
              initialState={{
                sorting: {
                  sortModel: [{ field: 'category', sort: 'asc' }],
                },
              }}
              processRowUpdate={(newRow, oldRow) => {
                const field = Object.keys(newRow).find(key => newRow[key] !== oldRow[key]);
                if (field && ['nok', 'sek', 'dkk', 'eur'].includes(field)) {
                  handleCellEditCommit({
                    id: newRow.id,
                    field,
                    value: newRow[field]
                  });
                }
                return newRow;
              }}
              onProcessRowUpdateError={(error) => {
                console.error('Error updating row:', error);
                showSnackbar('Failed to update row', 'error');
              }}
              sx={{
                '& .MuiDataGrid-cell--editing': {
                  bgcolor: 'rgb(255,215,115, 0.19)',
                  color: '#1a3e72',
                },
              }}
            />
          </Box>
          
          {/* Only show save button in the non-modal view (tab view) */}
          {!isModal && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveOtherPrices}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={loading || !isDataChanged}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
          
          {/* Only show save button in modal view when it's within the content (alternative design) */}
          {isModal && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveOtherPrices}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={loading || !isDataChanged}
              >
                {loading ? 'Saving...' : 'Save Other Prices'}
              </Button>
            </Box>
          )}
        </Paper>
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }
};

export default OtherPricesPanel;
